import * as stylex from '@stylexjs/stylex'
import type { ReactNode } from 'react'

import { breakpoints, colors, fonts, fontSizes, motion, radii, spacing } from '../tokens/tokens.stylex'
import { Heading, Text } from '../typography'

const styles = stylex.create({
  alert: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.borderStrong,
    borderRadius: radii.small,
    padding: spacing.space12,
    color: colors.foreground,
    fontFamily: fonts.sans,
    fontSize: fontSizes.label,
  },
  danger: { backgroundColor: colors.dangerSurface, borderColor: colors.danger, color: colors.danger },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.space8,
    alignItems: 'flex-start',
    paddingBlock: spacing.space32,
  },
  compact: { paddingBlock: spacing.space16 },
  spinner: {
    display: 'inline-block',
    width: spacing.space16,
    height: spacing.space16,
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: colors.borderStrong,
    borderTopColor: colors.foreground,
    borderRadius: radii.pill,
    animationName: stylex.keyframes({ to: { transform: 'rotate(360deg)' } }),
    animationDuration: motion.spinnerDuration,
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
    animationPlayState: { default: 'running', [breakpoints.reducedMotion]: 'paused' },
  },
  inverse: { borderColor: colors.inverseMuted, borderTopColor: colors.inverse },
})

export type AlertProps = {
  children: ReactNode
  tone?: 'default' | 'danger'
  announcement?: 'polite' | 'assertive' | 'off'
}

/**
 * Present supplied feedback with a polite, assertive, or silent announcement. Use EmptyState when a region
 * has no records rather than an error. Applications own requests, retries, and whether previous content
 * remains visible.
 *
 * Status: supported.
 */
export function Alert({ children, tone = 'default', announcement = 'polite' }: AlertProps) {
  return (
    <div
      role={announcement === 'assertive' ? 'alert' : 'status'}
      aria-live={announcement}
      {...stylex.props(styles.alert, tone === 'danger' && styles.danger)}
    >
      {children}
    </div>
  )
}

export type EmptyStateProps = {
  title: string
  titleLevel?: 1 | 2 | 3 | 4 | 5 | 6
  size?: 'default' | 'compact'
  children?: ReactNode
}

/**
 * Explain a region with no content and offer the application's next action. Use Alert for an error alongside
 * usable content. Applications supply the title, explanation, and action.
 *
 * Status: supported.
 */
export function EmptyState({ title, titleLevel = 2, size = 'default', children }: EmptyStateProps) {
  return (
    <div {...stylex.props(styles.empty, size === 'compact' && styles.compact)}>
      <Heading level={titleLevel} variant={size === 'compact' ? 'section' : 'title'}>
        {title}
      </Heading>
      {children && (
        <Text as="div" tone="muted" size={size === 'compact' ? 'small' : 'body'}>
          {children}
        </Text>
      )}
    </div>
  )
}

export type SpinnerProps = { tone?: 'default' | 'inverse' }

/**
 * Add decorative progress beside visible status text or an accessibly named control. Use Alert for a textual
 * announcement. Applications own pending state and supply its accessible meaning; the spinner itself is
 * hidden from assistive technology.
 *
 * Status: supported.
 */
export function Spinner({ tone = 'default' }: SpinnerProps) {
  return <span aria-hidden="true" {...stylex.props(styles.spinner, tone === 'inverse' && styles.inverse)} />
}
